import is from '@sindresorhus/is';
import { createContext, useContext, useEffect, useRef } from 'react';

import { createLoadSkillsBySearchTermRequest } from './api/load-skills-by-search-term-request';
import { createFeedbackDialogStore } from './feedback-dialog-store';
import { createTranslatedFeedbackDialogLabels } from './feedback-dialog-store-labels';
import { createFeedbackEmitter } from './feedback-emitter';
import { ApplicationContext } from '../../../shared/source/context/context';
import { useApplicationFetchers } from '../fetchers/use-application-fetchers';

import type { FeedbackDialogStore } from './feedback-dialog-store';
import type { FeedbackEmitterDependencies } from './feedback-emitter';
import type { FunctionComponent, PropsWithChildren } from 'react';
import type { StoreApi } from 'zustand';

export const FeedbackDialogContext = createContext<StoreApi<FeedbackDialogStore> | null>(null);

export const FeedbackDialogContextProvider: FunctionComponent<PropsWithChildren> = (properties) => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const storeRef = useRef<StoreApi<FeedbackDialogStore>>(undefined);

    const { changeSkillFeedback, reportSkillFeedback, requestSkillFeedback } = useApplicationFetchers();

    if (is.undefined(storeRef.current)) {
        const sendRequestSkill: FeedbackEmitterDependencies['sendRequestSkill'] = async (params) => {
            return requestSkillFeedback(params);
        };

        const sendChangeSkill: FeedbackEmitterDependencies['sendChangeSkill'] = async (params) => {
            return changeSkillFeedback(params);
        };

        const sendReportSkill: FeedbackEmitterDependencies['sendReportSkill'] = async (params) => {
            return reportSkillFeedback(params);
        };

        storeRef.current = createFeedbackDialogStore({
            feedbackEmitter: createFeedbackEmitter({
                loadSkillsBySearchTerm: createLoadSkillsBySearchTermRequest(validatingCobrainerApiFetcher),
                sendChangeSkill,
                sendReportSkill,
                sendRequestSkill,
            }),
            getFeedbackDialogStoreLabels: createTranslatedFeedbackDialogLabels(translate),
            mailTo: actions.mailTo,
            selectedLanguage: applicationState.language.code,
        });
    }

    useEffect(() => {
        const store = storeRef.current;
        if (is.nullOrUndefined(store)) {
            return;
        }

        store.getState().updateStoreLabelsTranslations();
    }, [applicationState.language.code]);

    return (
        <FeedbackDialogContext.Provider value={storeRef.current}>{properties.children}</FeedbackDialogContext.Provider>
    );
};
